// Frame.io migration (Storage Phase C) on src/ui. Admin-only, internal-only, opened from the Storage header's
// "Frame.io migration" button: a decommissioning tool used a handful of times, so it stays out of the page body.
//
// The flow is strictly: connect, paste a URL, PRE-FLIGHT, confirm, transfer. Nothing moves until the operator has seen
// the counts and pressed the button, and a merge into an existing folder is always said, never quiet.
// Calls (unchanged): GET BASE/status, GET BASE/migrations?clientId (polled every 5s while one is running or confirmed,
// only while this is open), POST BASE/connect, POST BASE/disconnect, POST BASE/preflight, POST BASE/migrations,
// POST BASE/migrations/:id/run.

import { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, ChevronDown, Plug, RotateCw, Search, Unplug } from 'lucide-react';
import api from '../lib/api';
import { formatBytes } from '../lib/folderUpload';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Tag from '../ui/Tag';
import { useConfirm } from '../ui/Confirm';
import { clean } from '../shell/copy';

const BASE = '/api/storage-archive/frameio';

const errMsg = (err, fallback) => clean(err?.response?.data?.error || err?.message) || fallback;
const plural = (n, one, many = `${one}s`) => `${Number(n || 0).toLocaleString()} ${n === 1 ? one : many}`;

// Long-running job state: a Badge, driven by the existing polling.
const STATUS = {
  confirmed: { tone: 'info', label: 'Queued' },
  pending:   { tone: 'info', label: 'Queued' },
  running:   { tone: 'info', label: 'Running' },
  complete:  { tone: 'ok', label: 'Complete' },
  failed:    { tone: 'danger', label: 'Failed' },
};
function StatusBadge({ status }) {
  const s = STATUS[status] || { tone: 'info', label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown' };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

function Disclosure({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ui-stack ui-stack--tight">
      <span>
        <button type="button" className="ui-disclosure ui-dense ui-text-2" aria-expanded={open} onClick={() => setOpen(v => !v)}>
          <Icon icon={ChevronDown} />{label}
        </button>
      </span>
      {open && <ul className="ui-plainlist ui-scroll ui-dense ui-text-2">{children}</ul>}
    </div>
  );
}

// ── Pre-flight review (spec §6.3) ─────────────────────────────────────────────

function PreflightReview({ preflight: p, rootName, setRootName }) {
  return (
    <div className="ui-stack">
      <p className="ui-p ui-dense ui-text-2">Nothing has been copied yet. Review this before starting.</p>
      {/* The name came from the API, not from the URL. Editable, but the rename applies to the root folder only. */}
      <Input label="Destination folder name" value={rootName} onChange={e => setRootName(e.target.value)} autoComplete="off"
        hint={`From Frame.io: ${clean(p.derivedName) || 'no name'}. Renaming applies to this top folder only; everything inside keeps its Frame.io name.`} />
      <dl className="ui-kv">
        <dt>Files</dt><dd>{Number(p.fileCount || 0).toLocaleString()}</dd>
        <dt>Folders</dt><dd>{Number(p.folderCount || 0).toLocaleString()}</dd>
        <dt>New folders</dt><dd>{Number(p.foldersNew || 0).toLocaleString()}</dd>
        <dt>Total size</dt><dd className="ui-tc">{formatBytes(p.totalBytes)}</dd>
      </dl>

      {/* A merge is NEVER silent. */}
      {p.mergesIntoExisting && (
        <div className="ui-stack ui-stack--tight">
          <p className="ui-p ui-dense">
            <Badge tone="warn">Merges</Badge> This merges into {plural(p.mergeTargetCount, 'folder')} that already exist.
            {p.rootIsMerge && ` The destination folder ${rootName} already exists: its contents are added to, not replaced.`}
          </p>
          <ul className="ui-plainlist ui-scroll ui-dense ui-text-2">
            {(p.mergeTargets || []).map(t => <li key={t}>{clean(t)}</li>)}
            {p.mergeTargetCount > (p.mergeTargets || []).length && <li>and {p.mergeTargetCount - p.mergeTargets.length} more</li>}
          </ul>
        </div>
      )}

      {p.capBreach && <p className="ui-p ui-dense" role="alert"><Badge tone="danger">Folder limit exceeded</Badge> {clean(p.capBreach.message)}</p>}
      {p.dryRunError && <p className="ui-p ui-dense" role="alert"><Badge tone="danger">Cannot create the folder tree</Badge> {clean(p.dryRunError.message)}</p>}
      {p.truncated && <p className="ui-p ui-dense" role="alert"><Badge tone="danger">Too large</Badge> That tree is too large to walk in one pass. Migrate one project at a time.</p>}

      {p.skippedCount > 0 && (
        <Disclosure label={`${plural(p.skippedCount, 'item')} will be skipped`}>
          {(p.skipped || []).map((s, i) => <li key={i}>{clean(s.name || s.id)}: {clean(s.reason)}</li>)}
        </Disclosure>
      )}
      {p.sanitizedNames?.length > 0 && (
        <Disclosure label={`${plural(p.sanitizedNames.length, 'folder name')} will be cleaned up`}>
          {p.sanitizedNames.map((s, i) => <li key={i}>{clean(s.from)} becomes {clean(s.to)}</li>)}
        </Disclosure>
      )}

      {/* Observed, not assumed: the live answer to whether Frame.io hands back version stacks at all. */}
      {p.versionStackCount > 0 && (
        <p className="ui-p ui-meta">
          {plural(p.versionStackCount, 'version stack')} found. The current version of each is migrated; earlier versions are not.
        </p>
      )}
    </div>
  );
}

// ── One migration ────────────────────────────────────────────────────────────

function MigrationRow({ m, onResume }) {
  const pct = m.filesTotal ? Math.round((m.filesDone / m.filesTotal) * 100) : 0;
  const canResume = m.status === 'failed' || (m.status !== 'running' && m.filesFailed > 0);
  return (
    <li className="ui-stack ui-stack--tight">
      <span className="ui-cluster">
        <span className="ui-dense">{clean(m.rootName)}</span>
        <StatusBadge status={m.status} />
        <span className="ui-meta">
          {Number(m.filesDone || 0).toLocaleString()} of {plural(m.filesTotal, 'file')}
          {m.filesFailed > 0 && `, ${m.filesFailed} failed`}, {formatBytes(Number(m.bytesDone))} of {formatBytes(Number(m.bytesTotal))}
        </span>
        {canResume && <Button variant="quiet" icon={RotateCw} onClick={() => onResume(m.id)}>Resume</Button>}
      </span>
      <span className={`ui-meter${m.status === 'failed' ? ' ui-meter--danger' : ''}`} role="progressbar" aria-label={`${clean(m.rootName)} progress`}
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <span className="ui-meter__fill" style={{ width: `${pct}%` }} />
      </span>
      {m.error && <span className="ui-field__error">{clean(m.error)}</span>}
    </li>
  );
}

// ── The dialog ───────────────────────────────────────────────────────────────

export default function FrameioMigrationPanel({ open, onClose, clientId, selectedFolderId, onMigrated }) {
  const confirmDialog = useConfirm();
  const [status, setStatus]         = useState(null);
  const [url, setUrl]               = useState('');
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState(null);
  const [preflight, setPreflight]   = useState(null);
  const [rootName, setRootName]     = useState('');
  const [migrations, setMigrations] = useState([]);
  const pollRef = useRef(null);

  const loadStatus = useCallback(() => {
    api.get(`${BASE}/status`).then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, []);
  const loadMigrations = useCallback(() => {
    if (!clientId) { setMigrations([]); return; }
    api.get(`${BASE}/migrations`, { params: { clientId } })
      .then(({ data }) => setMigrations(data.rows || []))
      .catch(() => {});
  }, [clientId]);

  useEffect(() => { if (open) { loadStatus(); loadMigrations(); } }, [open, loadStatus, loadMigrations]);

  // Poll only while something is actually moving: a 15 TB job runs for days.
  useEffect(() => {
    const active = migrations.some(m => m.status === 'running' || m.status === 'confirmed');
    clearInterval(pollRef.current);
    if (open && active) pollRef.current = setInterval(loadMigrations, 5000);
    return () => clearInterval(pollRef.current);
  }, [open, migrations, loadMigrations]);

  const connect = async () => {
    setError(null);
    try {
      const { data } = await api.post(`${BASE}/connect`);
      // Adobe's consent screen has to be a real top-level navigation in a new tab.
      window.open(data.authorizeUrl, '_blank', 'noopener');
    } catch (err) { setError(errMsg(err, 'The Frame.io connection did not start. Try again.')); }
  };

  const disconnect = async () => {
    const ok = await confirmDialog({
      title: 'Disconnect Frame.io?',
      body: 'Any running migration will start failing.',
      confirmLabel: 'Disconnect',
      destructive: true,
    });
    if (!ok) return;
    try { await api.post(`${BASE}/disconnect`); loadStatus(); }
    catch (err) { setError(errMsg(err, 'Frame.io was not disconnected. Try again.')); }
  };

  const runPreflight = async () => {
    setError(null); setBusy(true);
    try {
      const { data } = await api.post(`${BASE}/preflight`, { clientId, parentFolderId: selectedFolderId || null, url: url.trim() });
      setPreflight(data);
      setRootName(data.rootName || data.derivedName || '');
    } catch (err) {
      setError(errMsg(err, 'The pre-flight check failed. Check the URL, then try again.'));
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`${BASE}/migrations`, {
        clientId,
        parentFolderId: selectedFolderId || null,
        // The opaque pre-flight id lets the server reuse its OWN stored tree walk instead of re-walking Frame.io.
        preflightId: preflight.preflightId || null,
        accountId: preflight.accountId,
        folderId:  preflight.folderId,
        url:       preflight.sourceUrl,
        rootName:  rootName.trim(),
        expected:  { fileCount: preflight.fileCount },
      });
      setPreflight(null); setUrl('');
      loadMigrations();
      onMigrated?.();
    } catch (err) {
      setError(errMsg(err, 'The migration did not start. Try again.'));
      setPreflight(null);
    } finally { setBusy(false); }
  };

  const resume = async id => {
    try { await api.post(`${BASE}/migrations/${id}/run`); loadMigrations(); }
    catch (err) { setError(errMsg(err, 'The migration did not resume. Try again.')); }
  };

  const connected  = status?.connected;
  const configured = status?.configured;
  const canPreflight = connected && !!clientId && !!url.trim() && !busy;

  if (preflight) {
    const blocked = preflight.blocked;
    return (
      <Modal open={open} onClose={() => !busy && setPreflight(null)} title="Migrate from Frame.io"
        footer={(
          <>
            <Button variant="quiet" icon={ArrowLeft} disabled={busy} onClick={() => setPreflight(null)}>Back</Button>
            <Button variant="primary" loading={busy} disabled={blocked || !rootName.trim()} onClick={confirm}>
              Migrate {plural(preflight.fileCount, 'file')}
            </Button>
          </>
        )}>
        <PreflightReview preflight={preflight} rootName={rootName} setRootName={setRootName} />
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Frame.io migration">
      <div className="ui-stack">
        <p className="ui-p ui-dense ui-text-2">Internal tool: copies a Frame.io folder tree into this project's archive.</p>
        {error && <p className="ui-p ui-field__error" role="alert">{error}</p>}

        <div className="ui-cluster">
          {connected ? <Badge tone="ok">Connected</Badge> : <Tag>Not connected</Tag>}
          {connected && status.adobeEmail && <span className="ui-meta">{clean(status.adobeEmail)}</span>}
          {connected && status.rotationCount > 0 && <span className="ui-meta">Token rotated {plural(status.rotationCount, 'time')}</span>}
          <span className="ui-spacer" />
          {!connected && <Button variant="primary" icon={Plug} disabled={!configured} onClick={connect}>Connect Frame.io</Button>}
          {connected && <Button variant="quiet" icon={Unplug} onClick={disconnect}>Disconnect</Button>}
        </div>

        {!configured && status && (
          <p className="ui-p ui-dense" role="alert">
            <Badge tone="danger">Not configured</Badge> Frame.io is not configured on this server: {(status.missing || []).join(', ') || 'credentials'} missing.
            An administrator needs to add the Adobe credentials to the API environment.
          </p>
        )}

        <form className="ui-stack ui-stack--tight" onSubmit={e => { e.preventDefault(); if (canPreflight) runPreflight(); }}>
          <Input label="Frame.io folder URL" value={url} onChange={e => setUrl(e.target.value)} disabled={!connected} autoComplete="off"
            placeholder="Paste a Frame.io folder URL"
            hint={!clientId ? 'Select a project first.' : selectedFolderId ? 'Created inside the selected folder.' : 'Created at the project root.'} />
          <span><Button type="submit" variant="secondary" icon={Search} loading={busy} disabled={!canPreflight}>Check contents</Button></span>
        </form>

        {migrations.length > 0 && (
          <section className="ui-section" aria-labelledby="frameio-migrations">
            <h3 className="ui-heading" id="frameio-migrations">Migrations</h3>
            <ul className="ui-plainlist ui-stack">
              {migrations.map(m => <MigrationRow key={m.id} m={m} onResume={resume} />)}
            </ul>
          </section>
        )}
      </div>
    </Modal>
  );
}
